import { faTriangle } from '@fortawesome/pro-regular-svg-icons/faTriangle';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { PropertiesStore } from '@hortplus/properties-react';
import mapboxgl from 'mapbox-gl';
import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { Button, Col, Popover, Row } from 'react-bootstrap';
import Map, { Layer, Marker, Source } from 'react-map-gl';
import { useBoundaryLayers } from '../hooks/useBoundaryLayers';
import { useMapState } from '../hooks/useMapState';
import PreviewButton, { monoStyle } from './PreviewButton';

const statusLabel = (status) => {
  switch (status) {
    case 'pre_trap_catch_threshold_met':
      return 'Threshold Met';
    case 'accumulation_threshold_met':
      return 'Threshold Met';
    case 'average_threshold_met':
      return 'Threshold Met';
    case 'near_accumulation_threshold':
      return 'Near Threshold';
    case 'good':
      return 'No Action Required';
    case 'sprayed':
      return 'Sprayed';
    case 'no_data':
      return 'Not Monitored';
    default:
      return 'Unknown';
  }
};

const PropertyOverviewMap = (props) => {
  const [mapStyle, setMapStyle] = useState(monoStyle);
  const [boundaryHoverInfo, setBoundaryHoverInfo] = useState();
  const [trapHoverInfo, setTrapHoverInfo] = useState({});
  const layers = useBoundaryLayers([0.2, 0.4, 4, 0.5]);
  const [
    boundaryData,
    trapData,
    viewport,
    setViewport,
    mapRef,
    mapRefWrapper,
    ,
    fitBounds
  ] = useMapState();

  const toPoint = () => {
    return mapRef.current
      .getMap()
      .project(new mapboxgl.LngLat(trapHoverInfo.y, trapHoverInfo.x));
  };

  return boundaryData.features.length > 0 ? (
    <Map
      ref={mapRefWrapper}
      initialViewState={viewport}
      onViewportChange={(nextViewport) => setViewport(nextViewport)}
      mapStyle={`mapbox://styles/hortplus/${mapStyle}`}
      interactiveLayerIds={[
        'layerStyleThresholdMet',
        'layerStyleNearThreshold',
        'layerStyleGood',
        'layerStyleNoData'
      ]}
      onMouseMove={(e) => {
        const classList = e.originalEvent.srcElement.classList;
        // Check if element is a trap icon,a marker container or none.
        if (
          classList &&
          (classList.contains('fa-triangle') ||
            classList.contains('mapboxgl-marker') ||
            classList.length === 0)
        ) {
          setBoundaryHoverInfo(null);
        } else {
          const hoveredFeature = e.features && e.features[0];
          setBoundaryHoverInfo(
            hoveredFeature
              ? {
                  feature: hoveredFeature,
                  x: e.originalEvent.offsetX,
                  y: e.originalEvent.offsetY
                }
              : null
          );
        }
      }}
      onClick={(e) => {
        const hoveredFeature = e.features && e.features[0];
        if (hoveredFeature && props.onClick)
          props.onClick(hoveredFeature.properties.id);
      }}
      mapboxAccessToken={process.env.REACT_APP_MAPBOX_ACCESS_TOKEN}
    >
      <div
        className='m-3'
        style={{
          position: 'absolute',
          top: 0,
          right: 0
        }}
      >
        <Button
          variant='light'
          onClick={() => fitBounds(null, { duration: 500 })}
          className='m-3 float-right'
        >
          Reset
        </Button>
      </div>

      <Source id='trappingZones' type='geojson' data={boundaryData}>
        {layers.map((layer) => (
          <Layer key={layer.id} {...layer} />
        ))}
      </Source>
      {/** Trap markers */}
      {trapData.map((trap) => {
        const zoomLevel = viewport.zoom;
        return (
          // viewport.zoom is not updating so need to set condition
          // to one that will always be true to display traps.
          // Original value - 13.5.
          zoomLevel > 1 && (
            <Marker
              key={trap.id}
              latitude={trap.location[0]}
              longitude={trap.location[1]}
              offsetTop={-18}
              offsetLeft={-8}
            >
              <FontAwesomeIcon
                style={{ color: trap.displayColour }}
                onMouseEnter={() => {
                  setBoundaryHoverInfo(null);
                  setTrapHoverInfo({
                    id: trap.id,
                    code: trap.code,
                    name: trap.name,
                    lureName: trap.lureName,
                    locationDescription: trap.locationDescription,
                    x: trap.location[0],
                    y: trap.location[1]
                  });
                }}
                onMouseLeave={() => {
                  setTrapHoverInfo({});
                }}
                icon={faTriangle}
                size='lg'
              />
            </Marker>
          )
        );
      })}
      {boundaryHoverInfo ? (
        <Popover
          id={boundaryHoverInfo.feature.properties.id}
          placement='auto'
          style={{
            left:
              viewport.width - boundaryHoverInfo.x < 200
                ? boundaryHoverInfo.x - 200
                : boundaryHoverInfo.x + 5,
            top:
              viewport.height - boundaryHoverInfo.y < 100
                ? boundaryHoverInfo.y - 90
                : boundaryHoverInfo.y,
            position: 'absolute',
            width: '200px',
            zIndex: 9,
            pointerEvents: 'none'
          }}
        >
          <Popover.Title>
            Production Site:{' '}
            <b>{boundaryHoverInfo.feature.properties.trappingZoneName}</b>
          </Popover.Title>
          <Popover.Content>
            Status:{' '}
            <b>{statusLabel(boundaryHoverInfo.feature.properties.status)}</b>
          </Popover.Content>
        </Popover>
      ) : null}
      {trapHoverInfo.id && (
        <Popover
          id={trapHoverInfo.name}
          placement='auto'
          style={{
            left: toPoint().x,
            top: toPoint().y,
            position: 'absolute',
            width: '200px',
            zIndex: 9,
            pointerEvents: 'none'
          }}
        >
          <Popover.Title>
            Name: <b>{trapHoverInfo.name}</b>
          </Popover.Title>
          <Popover.Content>
            <div>
              ID: <b>{trapHoverInfo.code}</b>
            </div>
            <div>
              Lure Name: <b>{trapHoverInfo.lureName}</b>
            </div>
            <div>
              Location: <b>{trapHoverInfo.locationDescription}</b>
            </div>
          </Popover.Content>
        </Popover>
      )}
      <PreviewButton
        setMapStyle={setMapStyle}
        mapStyle={mapStyle}
        viewport={viewport}
      />
    </Map>
  ) : (
    <Row className='pt-5'>
      <Col>
        <h4 className='text-center'>
          This property does not have any boundary data.
        </h4>
      </Col>
    </Row>
  );
};

PropertyOverviewMap.propTypes = {
  propertiesStore: PropTypes.instanceOf(PropertiesStore).isRequired,
  onClick: PropTypes.func
};

export default observer(PropertyOverviewMap);
